import { AfterContentInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ContentChildren, ElementRef, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Optional, Output, QueryList, SimpleChanges, forwardRef } from "@angular/core";
import { Subscription } from "rxjs";
import { CsTabButtonBase } from "./cs-tab-button-interface";
import { NavController } from "providers/nav-controller";
import { LoggingService } from "services/logging.service";

@Component({
    selector: 'cs-tab',
    templateUrl: 'cs-tab.html',
    host: { 'class': 'tab' },
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CsTabComponent implements AfterContentInit, OnDestroy, OnInit {
  @Input() selection: any;
  @Output() selectionChange: EventEmitter<any> = new EventEmitter<any>();

  // URL synchronization inputs
  @Input() urlParam?: string;  // Name of URL parameter to sync with
  @Input() urlValueMapping?: (value: any) => string;  // Convert value to URL string
  @Input() valueFromUrlMapping?: (urlValue: string) => any;  // Convert URL string to value

  @ContentChildren(forwardRef(() => CsTabButtonBase), { descendants: true }) buttonsComponents: QueryList<CsTabButtonBase>;

  @HostBinding('class.can-scroll-left') canScrollLeft = false;
  @HostBinding('class.can-scroll-right') canScrollRight = false;

  buttons: CsTabButtonBase[];
  private scrollListener: () => void;
  private buttonsChangeSub: Subscription;

  constructor(
    public elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
    private loggingService: LoggingService,
    @Optional() private navCtrl?: NavController
  ) {
  }

  ngOnInit() {
    // Initialize selection from URL if configured
    if (this.urlParam && this.navCtrl) {
      const currentParams = this.navCtrl.getCurrentPageParams();
      // Check if currentParams exists and has the URL parameter
      if (currentParams && currentParams[this.urlParam] !== undefined) {
        const urlValue = currentParams[this.urlParam];
        // Convert URL value to component value
        const value = this.valueFromUrlMapping
          ? this.valueFromUrlMapping(urlValue)
          : urlValue;

        // Set selection and emit change to update parent via two-way binding
        this.selection = value;
        this.selectionChange.emit(value);
        this.cdr.markForCheck();
      }
    }
  }

  private refreshSelection() {
    if (this.buttons) {
      for (let button of this.buttons) {
        if (button.getValue() === this.selection) {
          button.select();
        } else {
          button.deselect();
        }
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.selection) {
      this.refreshSelection();
    }
  }

  ngAfterContentInit() {
    this.buttons = this.buttonsComponents.toArray();
    this.refreshSelection();
    this.setupScrollListener();

    // subscribe to button changes (when buttons are dynamically added/removed via *ngFor)
    this.buttonsChangeSub = this.buttonsComponents.changes.subscribe(() => {
      this.buttons = this.buttonsComponents.toArray();
      this.refreshSelection();
    });
  }

  ngOnDestroy() {
    if (this.scrollListener) {
      const element = this.elementRef.nativeElement as HTMLElement;
      const scrollContainer = element.querySelector('.tab-scroll-container') as HTMLElement;
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', this.scrollListener);
      }
    }
    if (this.buttonsChangeSub) {
      this.buttonsChangeSub.unsubscribe();
    }
  }

  private setupScrollListener() {
    const element = this.elementRef.nativeElement as HTMLElement;
    const scrollContainer = element.querySelector('.tab-scroll-container') as HTMLElement;

    if (!scrollContainer) {
      return;
    }

    this.scrollListener = () => {
      this.updateScrollIndicators();
    };

    scrollContainer.addEventListener('scroll', this.scrollListener, { passive: true });

    // initial check (wait for DOM to be ready)
    setTimeout(() => this.updateScrollIndicators(), 0);
  }

  private updateScrollIndicators() {
    const element = this.elementRef.nativeElement as HTMLElement;
    const scrollContainer = element.querySelector('.tab-scroll-container') as HTMLElement;

    if (!scrollContainer) {
      return;
    }

    const scrollLeft = scrollContainer.scrollLeft;
    const scrollWidth = scrollContainer.scrollWidth;
    const clientWidth = scrollContainer.clientWidth;

    // can scroll left if we're not at the start
    const newCanScrollLeft = scrollLeft > 1;

    // can scroll right if we're not at the end (with 1px tolerance)
    const newCanScrollRight = scrollLeft < scrollWidth - clientWidth - 1;

    // only trigger change detection if values actually changed
    if (this.canScrollLeft !== newCanScrollLeft || this.canScrollRight !== newCanScrollRight) {
      this.canScrollLeft = newCanScrollLeft;
      this.canScrollRight = newCanScrollRight;
      this.cdr.markForCheck();
    }
  }

  select(button: CsTabButtonBase) {
    const newValue = button.getValue();
    this.selection = newValue;
    this.selectionChange.emit(this.selection);

    // Sync URL if configured
    if (this.urlParam && this.navCtrl) {
      const urlValue = this.urlValueMapping
        ? this.urlValueMapping(newValue)
        : String(newValue);

      // update the one URL parameter, merged against the page the update lands on: while a
      // pushed page is still opening that is the page underneath, not the top (df-19cb4bb87306)
      void this.navCtrl.patchCurrentPageParamsAsync({ [this.urlParam]: urlValue });
    }

    // a click can race ngAfterContentInit and arrive before this.buttons is populated;
    // refreshSelection handles undefined safely, but surface the race so we can tell
    // whether it persists in production rather than silently swallowing it.
    if (!this.buttons) {
      this.loggingService.trackSilentProblem('cs-tab.select-before-content-init', {
        selection: String(newValue),
      });
    }
    this.refreshSelection();

    // scroll selected button into view with margin
    this.scrollButtonIntoView(button);
  }

  private scrollButtonIntoView(button: CsTabButtonBase) {
    const element = this.elementRef.nativeElement as HTMLElement;
    const scrollContainer = element.querySelector('.tab-scroll-container') as HTMLElement;
    const buttonElement = (button as any).elementRef?.nativeElement as HTMLElement;

    if (!scrollContainer || !buttonElement) {
      return;
    }

    // wait for DOM to update
    setTimeout(() => {
      const containerRect = scrollContainer.getBoundingClientRect();
      const buttonRect = buttonElement.getBoundingClientRect();

      const margin = 100; // margin for autoscroll away from edges
      const scrollLeft = scrollContainer.scrollLeft;

      // calculate if button is too close to left edge
      const leftEdge = buttonRect.left - containerRect.left;
      if (leftEdge < margin) {
        // scroll left to bring button away from left edge
        scrollContainer.scrollTo({
          left: scrollLeft + leftEdge - margin,
          behavior: 'smooth'
        });
        return;
      }

      // calculate if button is too close to right edge
      const rightEdge = containerRect.right - buttonRect.right;
      if (rightEdge < margin) {
        // scroll right to bring button away from right edge
        scrollContainer.scrollTo({
          left: scrollLeft + (margin - rightEdge),
          behavior: 'smooth'
        });
      }
    }, 0);
  }
}